import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import moment from "moment";
import { getTransactionList, getContactList, getBudgetAnalysis, getMonthWiseIncomeExpense } from "../Apis/apis";
import ReactApexChart from "react-apexcharts";
import { API_RESP_STATUS } from "../../Constant/constant";
import AllStateContext from "../../context/allstateContext";
import AddDailog from "../Record/AddDailog";
import AccountDialog from "../Record/AccountDialog";
import DateDialog from "../../Common/DateDialog";
import TimeDialog from "../../Common/TimeDialog";

// ── Helper: coloured initials avatar ─────────────────────────────────────────
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
];
const avatarColor = (name = "") => {
  let n = 0;
  for (const c of name) n += c.charCodeAt(0);
  return AVATAR_GRADIENTS[n % AVATAR_GRADIENTS.length];
};
const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

// ── Summary stat card ─────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, gradient, borderColor, shadowColor, accent }) => (
  <div style={{
    borderRadius: "20px", padding: "22px 24px",
    background: gradient, border: `1px solid ${borderColor}`,
    boxShadow: `0 6px 28px ${shadowColor}`,
    position: "relative", overflow: "hidden", height: "100%",
  }}>
    <div style={{ position: "absolute", top: "-20px", right: "-20px", width: "90px", height: "90px", borderRadius: "50%", background: accent, opacity: 0.6 }} />
    <div style={{ position: "absolute", bottom: "-30px", left: "-10px", width: "70px", height: "70px", borderRadius: "50%", background: accent, opacity: 0.25 }} />
    <div style={{ fontSize: "0.7rem", fontWeight: "800", color: borderColor.replace("0.2", "1").replace("0.25", "1"), textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "8px" }}>
      {label}
    </div>
    <div style={{ fontSize: "2rem", fontWeight: "900", letterSpacing: "-0.03em", lineHeight: 1, color: "var(--text-primary)" }}>
      {value}
    </div>
    <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "8px" }}>{sub}</div>
  </div>
);

// ── Monthly Trend Chart ───────────────────────────────────────────────────────
const TrendChart = () => {
  const currentYear = moment().year();

  const { data: trendData, isLoading } = useQuery(
    ["monthlyTrend", currentYear],
    async () => {
      const response = await getMonthWiseIncomeExpense(currentYear);
      if (response.data.status === 200) return response.data.payload.data;
      return [];
    }
  );

  if (isLoading || !trendData || trendData.length === 0) return null;

  const months = trendData.map((d) => {
    const [y, m] = d.month.split("-");
    return moment(`${y}-${m}-01`).format("MMM");
  });
  const incomeData = trendData.map((d) => Number(d.totalIncome) || 0);
  const expenseData = trendData.map((d) => Number(d.totalExpense) || 0);

  const totalInc = incomeData.reduce((s, v) => s + v, 0);
  const totalExp = expenseData.reduce((s, v) => s + v, 0);
  const savingsRate = totalInc > 0 ? (((totalInc - totalExp) / totalInc) * 100).toFixed(1) : 0;

  const isDark = document.documentElement.getAttribute("data-theme") !== "light";

  const chartOptions = {
    chart: {
      type: "bar",
      background: "transparent",
      toolbar: { show: false },
      fontFamily: "inherit",
    },
    theme: { mode: isDark ? "dark" : "light" },
    colors: ["#10b981", "#ef4444"],
    plotOptions: {
      bar: { borderRadius: 6, columnWidth: "55%", dataLabels: { position: "top" } },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: months,
      labels: { style: { fontSize: "0.78rem", fontWeight: 600 } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (val) => `₹${(val / 1000).toFixed(0)}k`,
        style: { fontSize: "0.78rem" },
      },
    },
    grid: {
      borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
      strokeDashArray: 4,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontSize: "13px",
      fontWeight: 600,
      markers: { radius: 4 },
    },
    tooltip: {
      y: { formatter: (val) => `₹${val.toLocaleString("en-IN")}` },
      theme: isDark ? "dark" : "light",
    },
    fill: { opacity: 1 },
  };

  const chartSeries = [
    { name: "Income", data: incomeData },
    { name: "Expense", data: expenseData },
  ];

  return (
    <div className="glass-card mb-4" style={{ padding: "22px" }}>
      <div className="d-flex align-items-center justify-content-between mb-3" style={{ flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h6 style={{ fontWeight: "800", margin: 0, fontSize: "1rem" }}>📈 Income vs Expense Trend</h6>
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "2px" }}>{currentYear} · monthly breakdown</div>
        </div>
        <div style={{
          padding: "8px 16px", borderRadius: "12px",
          background: Number(savingsRate) >= 0
            ? "rgba(16,185,129,0.1)"
            : "rgba(239,68,68,0.1)",
          border: `1px solid ${Number(savingsRate) >= 0 ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.25)"}`,
        }}>
          <div style={{ fontSize: "0.68rem", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.08em", color: Number(savingsRate) >= 0 ? "rgb(16,185,129)" : "var(--danger)" }}>
            Savings Rate
          </div>
          <div style={{ fontSize: "1.3rem", fontWeight: "900", color: Number(savingsRate) >= 0 ? "rgb(16,185,129)" : "var(--danger)" }}>
            {savingsRate}%
          </div>
        </div>
      </div>
      <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={280} />
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
const Dashboard = () => {
  const history = useNavigate();
  const { openAccount, openDateDialog, openTimeDialog, setOpen, setRecordItem, startEndDate } = useContext(AllStateContext);

  const thisMonthStr = moment().startOf("month").format("YYYY-MM-DD");

  // The API internally does: moment(startDate).utc().startOf('day').add(1, 'day')
  // So to get June 1 as effective startDate, we must pass May 31 (month start minus 1 day).
  const monthStartMinus1 = moment().startOf("month").subtract(1, "day").toDate();
  const today = new Date();

  // Fetch transactions — current month
  const { data: txData, isLoading: txLoading, refetch } = useQuery(
    ["dashboardTx", moment().format("YYYY-MM")],
    async () => {
      const response = await getTransactionList({ }, monthStartMinus1, today, true);
      if (response.data.status === API_RESP_STATUS.success) return response.data.payload;
      return null;
    }
  );

  // Fetch contacts
  const { data: contactData, isLoading: contactLoading } = useQuery(
    ["dashboardContacts"],
    async () => {
      const response = await getContactList();
      if (response.data.status === API_RESP_STATUS.success) return response.data.payload;
      return null;
    }
  );

  // Fetch budget alerts (over-budget categories)
  const { data: budgetData } = useQuery(
    ["dashboardBudget"],
    async () => {
      const response = await getBudgetAnalysis({ type: 1 }, moment().format("YYYY-MM"), "", true);
      if (response.data.status === API_RESP_STATUS.success) return response.data.payload;
      return null;
    }
  );

  const income = txData?.totalInEx?.totalIncome || 0;
  const expense = txData?.totalInEx?.totalExpense || 0;
  const net = income - expense;

  const contacts = (contactData?.data || []).filter((c) => c.contactId);
  const totalDue = contacts.filter((c) => c.balance > 0).reduce((s, c) => s + (c.balance || 0), 0);
  const totalAdvance = contacts.filter((c) => c.balance < 0).reduce((s, c) => s + Math.abs(c.balance || 0), 0);
  const dueContacts = contacts.filter((c) => c.balance > 0).sort((a, b) => b.balance - a.balance).slice(0, 5);
  const advContacts = contacts.filter((c) => c.balance < 0).sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance)).slice(0, 5);

  const recentTx = (txData?.data || []).slice(0, 6);

  const overBudget = (budgetData?.data || []).filter(
    (item) => item.budgetDetail && item.spent >= item.budgetDetail.limit
  );
  const nearBudget = (budgetData?.data || []).filter(
    (item) => item.budgetDetail && item.spent / item.budgetDetail.limit >= 0.85 && item.spent < item.budgetDetail.limit
  );

  if (txLoading || contactLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4" style={{ paddingBottom: "60px", paddingTop: "24px" }}>
      <AddDailog fetchData={refetch} />
      {openAccount && <AccountDialog />}
      {openDateDialog && <DateDialog />}
      {openTimeDialog && <TimeDialog />}

      {/* ── Top bar: date + Add button ──────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-4" style={{ flexWrap: "wrap", gap: "12px" }}>
        <div>
          <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {moment().format("dddd, D MMMM YYYY")}
          </div>
          <div style={{ fontSize: "1.4rem", fontWeight: "900", letterSpacing: "-0.02em", marginTop: "2px" }}>
            {moment().hour() < 12 ? "Good Morning" : moment().hour() < 17 ? "Good Afternoon" : "Good Evening"} 👋
          </div>
        </div>
        <button
          className="btn btn-primary"
          style={{ padding: "10px 22px", fontWeight: "700", borderRadius: "12px", background: "linear-gradient(135deg, var(--primary) 0%, #8b5cf6 100%)", border: "none", boxShadow: "0 4px 14px rgba(99,102,241,0.35)", whiteSpace: "nowrap", flexShrink: 0 }}
          onClick={() => { setOpen(true); setRecordItem(""); }}
        >
          + Add Transaction
        </button>
      </div>

      {/* ── 4 Summary Cards ──────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <StatCard
            label="↓ Income"
            value={`₹${income.toLocaleString("en-IN")}`}
            sub="earned this month"
            gradient="linear-gradient(135deg, rgba(16,185,129,0.14) 0%, rgba(16,185,129,0.04) 100%)"
            borderColor="rgba(16,185,129,0.25)"
            shadowColor="rgba(16,185,129,0.1)"
            accent="rgba(16,185,129,0.15)"
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            label="↑ Expense"
            value={`₹${expense.toLocaleString("en-IN")}`}
            sub="spent this month"
            gradient="linear-gradient(135deg, rgba(239,68,68,0.14) 0%, rgba(239,68,68,0.04) 100%)"
            borderColor="rgba(239,68,68,0.25)"
            shadowColor="rgba(239,68,68,0.1)"
            accent="rgba(239,68,68,0.15)"
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            label="⚖ Net Balance"
            value={`${net < 0 ? "-" : ""}₹${Math.abs(net).toLocaleString("en-IN")}`}
            sub={net >= 0 ? "surplus" : "deficit"}
            gradient={net >= 0
              ? "linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(99,102,241,0.04) 100%)"
              : "linear-gradient(135deg, rgba(239,68,68,0.14) 0%, rgba(239,68,68,0.04) 100%)"}
            borderColor={net >= 0 ? "rgba(99,102,241,0.25)" : "rgba(239,68,68,0.25)"}
            shadowColor={net >= 0 ? "rgba(99,102,241,0.1)" : "rgba(239,68,68,0.08)"}
            accent={net >= 0 ? "rgba(99,102,241,0.15)" : "rgba(239,68,68,0.15)"}
          />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard
            label="👥 Contact Due"
            value={`₹${totalDue.toLocaleString("en-IN")}`}
            sub={`${contacts.filter((c) => c.balance > 0).length} people owe you`}
            gradient="linear-gradient(135deg, rgba(245,158,11,0.14) 0%, rgba(245,158,11,0.04) 100%)"
            borderColor="rgba(245,158,11,0.25)"
            shadowColor="rgba(245,158,11,0.1)"
            accent="rgba(245,158,11,0.15)"
          />
        </div>
      </div>

      {/* ── Budget Alerts ────────────────────────────────────────────────── */}
      {(overBudget.length > 0 || nearBudget.length > 0) && (
        <div className="mb-4">
          {overBudget.length > 0 && (
            <div style={{
              background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.2)",
              borderLeft: "4px solid var(--danger)", borderRadius: "14px", padding: "14px 18px",
              marginBottom: "10px", display: "flex", alignItems: "center", gap: "12px",
            }}>
              <span style={{ fontSize: "1.3rem" }}>🚨</span>
              <div>
                <div style={{ fontWeight: "800", fontSize: "0.88rem", color: "var(--danger)" }}>
                  {overBudget.length} budget{overBudget.length > 1 ? "s" : ""} exceeded!
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "2px" }}>
                  {overBudget.map((b) => b.name).join(", ")}
                </div>
              </div>
              <button className="btn btn-sm ms-auto" style={{ borderRadius: "8px", background: "var(--danger)", color: "#fff", fontSize: "0.78rem", fontWeight: "700", padding: "4px 14px" }} onClick={() => history("/budgets")}>
                View
              </button>
            </div>
          )}
          {nearBudget.length > 0 && (
            <div style={{
              background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.2)",
              borderLeft: "4px solid #f59e0b", borderRadius: "14px", padding: "14px 18px",
              display: "flex", alignItems: "center", gap: "12px",
            }}>
              <span style={{ fontSize: "1.3rem" }}>⚠️</span>
              <div>
                <div style={{ fontWeight: "800", fontSize: "0.88rem", color: "#f59e0b" }}>
                  {nearBudget.length} budget{nearBudget.length > 1 ? "s" : ""} near limit
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "2px" }}>
                  {nearBudget.map((b) => b.name).join(", ")}
                </div>
              </div>
              <button className="btn btn-sm ms-auto" style={{ borderRadius: "8px", background: "#f59e0b", color: "#fff", fontSize: "0.78rem", fontWeight: "700", padding: "4px 14px" }} onClick={() => history("/budgets")}>
                View
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Monthly Trend Chart ──────────────────────────────────────────── */}
      <TrendChart />

      {/* ── Bottom section: Contacts + Recent Transactions ───────────────── */}
      <div className="row g-4">

        {/* Left: Contact overview */}
        <div className="col-12 col-lg-5">
          <div className="glass-card" style={{ padding: "22px" }}>
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h6 style={{ fontWeight: "800", margin: 0, fontSize: "1rem" }}>👥 Contacts</h6>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "2px" }}>{contacts.length} total</div>
              </div>
              <button className="btn btn-secondary" style={{ fontSize: "0.78rem", padding: "5px 14px", borderRadius: "8px", fontWeight: "600", borderColor: "var(--border-color)" }} onClick={() => history("/contact")}>
                View All →
              </button>
            </div>

            {/* Totals row */}
            <div className="row g-2 mb-4">
              <div className="col-6">
                <div style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.18)", borderRadius: "12px", padding: "12px 14px" }}>
                  <div style={{ fontSize: "0.68rem", fontWeight: "800", color: "var(--danger)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Total Due</div>
                  <div style={{ fontSize: "1.2rem", fontWeight: "900", color: "var(--danger)", marginTop: "4px" }}>₹{totalDue.toLocaleString("en-IN")}</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>{contacts.filter((c) => c.balance > 0).length} people owe you</div>
                </div>
              </div>
              <div className="col-6">
                <div style={{ background: "rgba(5,150,105,0.06)", border: "1px solid rgba(5,150,105,0.18)", borderRadius: "12px", padding: "12px 14px" }}>
                  <div style={{ fontSize: "0.68rem", fontWeight: "800", color: "var(--success)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Advance Given</div>
                  <div style={{ fontSize: "1.2rem", fontWeight: "900", color: "var(--success)", marginTop: "4px" }}>₹{totalAdvance.toLocaleString("en-IN")}</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>{contacts.filter((c) => c.balance < 0).length} you owe</div>
                </div>
              </div>
            </div>

            {/* Due contacts list */}
            {dueContacts.length > 0 && (
              <>
                <div style={{ fontSize: "0.7rem", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "10px" }}>
                  Top Due Contacts
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {dueContacts.map((c) => (
                    <div key={c.contactId}
                      onClick={() => history("/contact")}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", borderRadius: "10px", background: "var(--bg-surface)", border: "1px solid var(--border-color)", cursor: "pointer", transition: "all 0.15s" }}
                    >
                      <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: avatarColor(c.name), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: "800", color: "#fff", flexShrink: 0 }}>
                        {getInitials(c.name)}
                      </div>
                      <div style={{ flex: 1, fontWeight: "700", fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                      <div style={{ fontSize: "0.85rem", fontWeight: "800", color: "var(--danger)", flexShrink: 0 }}>
                        ₹{c.balance.toLocaleString("en-IN")}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Advance contacts list */}
            {advContacts.length > 0 && (
              <>
                <div style={{ fontSize: "0.7rem", fontWeight: "800", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "10px", marginTop: "16px" }}>
                  You Owe
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {advContacts.map((c) => (
                    <div key={c.contactId}
                      onClick={() => history("/contact")}
                      style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", borderRadius: "10px", background: "var(--bg-surface)", border: "1px solid var(--border-color)", cursor: "pointer", transition: "all 0.15s" }}
                    >
                      <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: avatarColor(c.name), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: "800", color: "#fff", flexShrink: 0 }}>
                        {getInitials(c.name)}
                      </div>
                      <div style={{ flex: 1, fontWeight: "700", fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                      <div style={{ fontSize: "0.85rem", fontWeight: "800", color: "var(--success)", flexShrink: 0 }}>
                        ₹{Math.abs(c.balance).toLocaleString("en-IN")}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {contacts.length === 0 && (
              <div className="text-center text-muted py-4" style={{ fontSize: "0.88rem" }}>
                No contacts yet.
              </div>
            )}
          </div>
        </div>

        {/* Right: Recent Transactions */}
        <div className="col-12 col-lg-7">
          <div className="glass-card" style={{ padding: "22px" }}>
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h6 style={{ fontWeight: "800", margin: 0, fontSize: "1rem" }}>🧾 Recent Transactions</h6>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "2px" }}>This month's last 6</div>
              </div>
              <button className="btn btn-secondary" style={{ fontSize: "0.78rem", padding: "5px 14px", borderRadius: "8px", fontWeight: "600", borderColor: "var(--border-color)" }} onClick={() => history("/record")}>
                View All →
              </button>
            </div>

            {recentTx.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {recentTx.map((item) => (
                  <div key={item.transactionId}
                    style={{
                      display: "flex", alignItems: "center", gap: "12px",
                      padding: "12px 14px", borderRadius: "12px",
                      background: "var(--bg-surface)", border: "1px solid var(--border-color)",
                      transition: "all 0.15s",
                    }}
                  >
                    {/* Category icon */}
                    <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: item.type === 1 ? "rgba(239,68,68,0.08)" : "rgba(16,185,129,0.08)", border: item.type === 1 ? "1px solid rgba(239,68,68,0.2)" : "1px solid rgba(16,185,129,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {item.categoryDetails?.iconDetail?.icon ? (
                        <img style={{ height: "24px", width: "24px" }} src={item.categoryDetails.iconDetail.icon} alt={item.categoryDetails?.name} />
                      ) : (
                        <span style={{ fontSize: "1rem" }}>{item.type === 1 ? "↑" : "↓"}</span>
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: "700", fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.categoryDetails?.name || "Transaction"}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "1px", display: "flex", gap: "6px", alignItems: "center" }}>
                        <span>{moment(item.date).format("D MMM")}</span>
                        {item.accountDetails?.name && <><span>·</span><span>{item.accountDetails.name}</span></>}
                        {item.notes && <><span>·</span><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "120px" }}>{item.notes}</span></>}
                      </div>
                    </div>

                    <div style={{ fontWeight: "800", fontSize: "0.95rem", color: item.type === 1 ? "var(--danger)" : "var(--success)", flexShrink: 0 }}>
                      {item.type === 1 ? "-" : "+"}₹{Number(item.amount).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-5" style={{ fontSize: "0.9rem" }}>
                No transactions this month yet.
              </div>
            )}

            {/* Quick-action shortcuts */}
            <div className="d-flex gap-2 mt-4" style={{ flexWrap: "wrap" }}>
              {[
                { label: "📊 Analysis", path: "/analysis" },
                { label: "💰 Budgets", path: "/budgets" },
                { label: "🏦 Accounts", path: "/account" },
                { label: "🏷 Categories", path: "/categories" },
              ].map((item) => (
                <button
                  key={item.path}
                  className="btn btn-secondary"
                  style={{ fontSize: "0.78rem", fontWeight: "700", padding: "6px 14px", borderRadius: "10px", borderColor: "var(--border-color)", background: "var(--bg-surface-hover)", flex: "1", minWidth: "110px" }}
                  onClick={() => history(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
